import {getApiUrl} from "../config/api";

// src/services/photoService.ts
class PhotoService {
  private baseUrl = getApiUrl();
  // private baseUrl = import.meta.env.VITE_API_URL || "http://localhost:3001/api";

  // SSL error detection and fallback
  private async makeRequest(
    url: string,
    options: RequestInit
  ): Promise<Response> {
    try {
      console.log(`🌐 Attempting request to: ${url}`);
      const response = await fetch(url, options);
      return response;
    } catch (error: any) {
      console.error(`❌ Request failed:`, error);

      // Check if it's an SSL error
      if (
        error.message?.includes("SSL") ||
        error.message?.includes("certificate") ||
        error.message?.includes("ERR_SSL_UNRECOGNIZED_NAME_ALERT")
      ) {
        console.log("🔒 SSL Error detected, trying HTTP fallback...");

        // Try HTTP fallback for development
        if (import.meta.env.MODE === "development") {
          const httpUrl = url.replace("https://", "http://");
          console.log(`🔄 Retrying with HTTP: ${httpUrl}`);
          return fetch(httpUrl, options);
        }
      }

      throw error;
    }
  }

  async uploadPhotos(
    propertyId: number,
    files: File[],
    options: {
      isPrimary?: boolean;
      sortOrder?: number;
    } = {}
  ): Promise<any> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("photos", file);
      console.log(
        `📁 Added file: ${file.name} (${file.size} bytes, ${file.type})`
      );
    });

    if (options.isPrimary !== undefined) {
      formData.append("isPrimary", options.isPrimary.toString());
    }
    if (options.sortOrder !== undefined) {
      formData.append("sortOrder", options.sortOrder.toString());
    }

    const token = this.getAuthToken();
    const url = `${this.baseUrl}/property-photos/properties/${propertyId}/photos`;

    console.log(`📤 Upload URL: ${url}`);
    console.log(`🔑 Token available: ${!!token}`);

    try {
      const response = await this.makeRequest(url, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          // Don't set Content-Type for FormData
        },
        body: formData,
      });

      console.log(
        `📨 Response status: ${response.status} ${response.statusText}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Error response: ${errorText}`);
        throw new Error(`Upload failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Upload successful:`, result);
      return result;
    } catch (error) {
      console.error("❌ Upload error:", error);
      throw error;
    }
  }

  private getAuthToken(): string | null {
    // Your existing token logic
    try {
      const persistedState = localStorage.getItem("persist:root");
      if (persistedState) {
        const parsed = JSON.parse(persistedState);
        const auth = JSON.parse(parsed.auth || "{}");
        if (auth.token) {
          return auth.token;
        }
      }

      // Check direct localStorage
      const directToken = localStorage.getItem("auth_token");
      if (directToken) {
        return directToken;
      }

      return null;
    } catch (error) {
      console.error("❌ Failed to get auth token:", error);
      return null;
    }
  }
  async deletePhoto(photoId: number): Promise<boolean> {
    const token = this.getAuthToken();

    const response = await fetch(`${this.baseUrl}/property-photos/${photoId}`, {
      method: "DELETE",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Delete failed: ${response.status}`);
    }

    return true;
  }

  async setPrimaryPhoto(photoId: number): Promise<any> {
    const token = this.getAuthToken();

    const response = await fetch(
      `${this.baseUrl}/property-photos/${photoId}/set-primary`,
      {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Set primary failed: ${response.status}`
      );
    }

    return await response.json();
  }

  async getPropertyPhotos(propertyId: number): Promise<any[]> {
    const token = this.getAuthToken();

    const response = await fetch(
      `${this.baseUrl}/property-photos/properties/${propertyId}/photos`,
      {
        method: "GET",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Get photos failed: ${response.status}`
      );
    }

    return await response.json();
  }

  async reorderPhotos(propertyId: number, photoIds: number[]): Promise<any> {
    const token = this.getAuthToken();

    const response = await fetch(
      `${this.baseUrl}/property-photos/properties/${propertyId}/photos/reorder`,
      {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({photoIds}),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Reorder failed: ${response.status}`
      );
    }

    return await response.json();
  }
}

export const photoService = new PhotoService();
